import type { SnowplowIdentity } from '@nextmar/snowplow-sdk';
import { hasSensorsInitialized } from '../reliability/sensorsReadiness';
import { ZL_USERINFO } from '@/utils/constants';
import { isInZeloolApp } from '@/utils/tools';

/**
 * 神策身份在浏览器本地缓存中的 distinct_id key。
 */
const DISTINCT_ID_STORAGE_KEY = 'zl-distinct_id';

/**
 * 神策身份在浏览器本地缓存中的 anonymous_id key。
 */
const ANONYMOUS_ID_STORAGE_KEY = 'zl-anonymous_id';

/**
 * 后端接口透传使用的 distinct-id cookie key。
 */
const DISTINCT_ID_COOKIE_KEY = 'distinct-id';

/**
 * 后端接口透传使用的 anonymous-id cookie key。
 */
const ANONYMOUS_ID_COOKIE_KEY = 'anonymous-id';

/**
 * 神策身份 cookie 的有效期，保持与 axios 请求头写入逻辑一致。
 */
const IDENTITY_COOKIE_MAX_AGE_SECONDS = 31536000;

export interface SensorsIdentity {
  distinctId?: string;
  anonymousId?: string;
}

/** 只观察本窗口真实SDK的匿名ID，不读取缓存、cookie或父窗口。 */
export function readLiveSensorsAnonymousId(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return normalizeReportingIdentity(window.sensorsDataAnalytic201505?.quick?.('getAnonymousID'));
  } catch {
    return undefined;
  }
}

/**
 * 过滤神策身份中的无效空值。
 */
function normalizeIdentityValue(value: unknown): string | undefined {
  if (
    value === '' ||
    value === null ||
    value === undefined ||
    value === 'null' ||
    value === 'undefined'
  ) {
    return undefined;
  }

  return String(value);
}

/**
 * 安全读取 Storage，兼容隐私模式或跨端容器禁用 storage 的场景。
 */
function readStorageValue(storage: Storage | undefined, key: string): string | undefined {
  try {
    return normalizeIdentityValue(storage?.getItem(key));
  } catch {
    return undefined;
  }
}

/**
 * 安全获取 sessionStorage，兼容访问 storage getter 即抛错的浏览器模式。
 */
function getSessionStorage(): Storage | undefined {
  try {
    return globalThis.sessionStorage;
  } catch {
    return undefined;
  }
}

/**
 * 安全获取 localStorage，兼容访问 storage getter 即抛错的浏览器模式。
 */
function getLocalStorage(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

/**
 * 安全写入 Storage，避免隐私模式配额异常影响主链路。
 */
function writeStorageValue(
  storage: Storage | undefined,
  key: string,
  value: string | undefined,
): void {
  if (!value) return;

  try {
    storage?.setItem(key, value);
  } catch {
    // 身份缓存失败不应阻断神策或 Snowplow 上报。
  }
}

/**
 * 从 document.cookie 中读取指定身份字段。
 */
function readCookieValue(key: string): string | undefined {
  if (typeof document === 'undefined') return undefined;

  const matchedCookie = document.cookie.split('; ').find((cookie) => cookie.startsWith(`${key}=`));
  if (!matchedCookie) return undefined;

  return normalizeIdentityValue(decodeURIComponent(matchedCookie.slice(key.length + 1)));
}

/**
 * 写入身份 cookie，供服务端请求头和后续首屏 fallback 复用。
 */
function writeCookieValue(key: string, value: string | undefined): void {
  if (!value || typeof document === 'undefined') return;

  document.cookie = `${key}=${encodeURIComponent(value)}; path=/; max-age=${IDENTITY_COOKIE_MAX_AGE_SECONDS}; samesite=lax`;
}

/**
 * 从一个神策 SDK 实例中读取 distinct_id 与 anonymous_id。
 */
function readSensorsIdentityFromInstance(sensors: unknown): SensorsIdentity {
  const sensorsInstance = sensors as
    | {
        store?: { getDistinctId?: () => unknown };
        quick?: (command: string) => unknown;
      }
    | undefined;

  if (!sensorsInstance) return {};

  try {
    const distinctId = normalizeIdentityValue(sensorsInstance.store?.getDistinctId?.());
    const anonymousId = sensorsInstance.quick?.('getAnonymousID');
    return {
      distinctId,
      // 实际SDK初始化前可能返回false；非string哨兵不能被字符串化成冻结身份。
      anonymousId:
        typeof anonymousId === 'string' ? normalizeIdentityValue(anonymousId) : undefined,
    };
  } catch {
    return {};
  }
}

/**
 * 收集当前窗口和父窗口中可访问的神策 SDK 实例。
 */
function getSensorsInstances(): unknown[] {
  if (typeof window === 'undefined') return [];

  const instances: unknown[] = [window.sensorsDataAnalytic201505, window.sensors];

  try {
    if (window.parent && window.parent !== window) {
      instances.push(window.parent.sensorsDataAnalytic201505, window.parent.sensors);
    }
  } catch {
    // 跨域 iframe 访问 parent 可能抛错，此处只跳过 parent fallback。
  }

  return instances.filter(Boolean);
}

/**
 * 读取当前可用的神策身份，优先使用 SDK 实时值，再回退到 storage 与 cookie。
 */
export function getSensorsIdentity(): SensorsIdentity {
  const identity: SensorsIdentity = {};

  for (const sensors of getSensorsInstances()) {
    const sensorsIdentity = readSensorsIdentityFromInstance(sensors);
    identity.distinctId = identity.distinctId ?? sensorsIdentity.distinctId;
    identity.anonymousId = identity.anonymousId ?? sensorsIdentity.anonymousId;

    if (identity.distinctId && identity.anonymousId) {
      return identity;
    }
  }

  identity.distinctId =
    identity.distinctId ??
    readStorageValue(getSessionStorage(), DISTINCT_ID_STORAGE_KEY) ??
    readStorageValue(getLocalStorage(), DISTINCT_ID_STORAGE_KEY) ??
    readCookieValue(DISTINCT_ID_COOKIE_KEY);
  identity.anonymousId =
    identity.anonymousId ??
    readStorageValue(getSessionStorage(), ANONYMOUS_ID_STORAGE_KEY) ??
    readStorageValue(getLocalStorage(), ANONYMOUS_ID_STORAGE_KEY) ??
    readCookieValue(ANONYMOUS_ID_COOKIE_KEY);

  return identity;
}

/**
 * 将神策身份同步到本地缓存，供 Snowplow 首屏事件和 App H5 fallback 使用。
 */
export function syncSensorsIdentityToStorage(identity = getSensorsIdentity()): SensorsIdentity {
  writeStorageValue(getSessionStorage(), DISTINCT_ID_STORAGE_KEY, identity.distinctId);
  writeStorageValue(getSessionStorage(), ANONYMOUS_ID_STORAGE_KEY, identity.anonymousId);
  writeStorageValue(getLocalStorage(), DISTINCT_ID_STORAGE_KEY, identity.distinctId);
  writeStorageValue(getLocalStorage(), ANONYMOUS_ID_STORAGE_KEY, identity.anonymousId);
  writeCookieValue(DISTINCT_ID_COOKIE_KEY, identity.distinctId);
  writeCookieValue(ANONYMOUS_ID_COOKIE_KEY, identity.anonymousId);

  return identity;
}

/** 上报身份不完整的稳定原因，质量结果本身不决定是否发送。 */
export type ReportingIdentityReason =
  | 'sensors_not_initialized'
  | 'missing_identity_fields'
  | 'read_failed'
  | 'identity_conflict';
/** 默认使用当前有效登录身份；注册专用验证可只读取真实神策身份。 */
export interface ReportingIdentityOptions {
  useLoggedInIdentity?: boolean;
}
/** 公共上报身份结果，两项实际非空才能标记 complete。 */
export type ReportingIdentityResult =
  | {
      status: 'complete';
      identity: Readonly<Required<SnowplowIdentity>>;
      missingDistinctId: false;
      missingAnonymousId: false;
    }
  | {
      status: 'incomplete';
      identity: Readonly<SnowplowIdentity>;
      missingDistinctId: boolean;
      missingAnonymousId: boolean;
      reason: ReportingIdentityReason;
    };
/** 上报身份拒绝 ASCII/C1 控制字符及非字符串哨兵。 */
const REPORTING_IDENTITY_CONTROL_CHARACTERS = /[\u0000-\u001f\u007f-\u009f]/; // eslint-disable-line no-control-regex -- 身份必须拒绝控制字符

/**
 * 严格规范化上报身份，不把数字、布尔值或缓存空值转换成假身份。
 * @param value 项目来源返回的未知值。
 * @returns 有效非空字符串，无效时为 undefined。
 */
function normalizeReportingIdentity(value: unknown): string | undefined {
  if (typeof value !== 'string' || REPORTING_IDENTITY_CONTROL_CHARACTERS.test(value))
    return undefined;
  const normalized = value.trim();
  return normalized && !['null', 'undefined', 'false'].includes(normalized.toLowerCase())
    ? normalized
    : undefined;
}

/**
 * 读取当前可信 Browser 登录 UUID，集中复用回跳 cookie/token 及用户来源。
 * @returns 有效登录态的当前 UUID；SSR、App、登出或读取失败为 undefined，无入参。
 */
export function readReportingLoginUuid(): string | undefined {
  try {
    if (typeof window === 'undefined' || isInZeloolApp()) return undefined;
    const token =
      new URLSearchParams(window.location.search).get('token') || localStorage.getItem('zl-token');
    if (!normalizeReportingIdentity(token)) return undefined;
    const cookieUuid = normalizeReportingIdentity(readCookieValue('uuid'));
    if (cookieUuid) return cookieUuid;
    const user: unknown = JSON.parse(localStorage.getItem(ZL_USERINFO) ?? '{}');
    return user && typeof user === 'object' && !Array.isArray(user)
      ? normalizeReportingIdentity(Reflect.get(user, 'uuid'))
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * 每次同步读取同一已初始化神策实例的身份对；不拼接父窗口或历史缓存。
 * @param options 默认当前登录 UUID 优先；useLoggedInIdentity=false 用于注册实际神策账号校验。
 * @returns 冻结身份及完整性结果；缺失或异常保留已验证字段，ready 事件由 SDK 告警后继续发送。
 */
export function getReportingIdentity(
  options: ReportingIdentityOptions = {},
): ReportingIdentityResult {
  const initialized = hasSensorsInitialized();
  let distinctId = options.useLoggedInIdentity === false ? undefined : readReportingLoginUuid();
  let anonymousId: string | undefined;
  let readFailed = false;
  if (initialized && typeof window !== 'undefined') {
    let sensors: typeof window.sensorsDataAnalytic201505;
    try {
      sensors = window.sensorsDataAnalytic201505;
    } catch {
      readFailed = true;
    }
    try {
      const sdkDistinctId = normalizeReportingIdentity(sensors?.store?.getDistinctId?.());
      distinctId ??= sdkDistinctId;
    } catch {
      readFailed = true;
    }
    try {
      anonymousId = normalizeReportingIdentity(sensors?.quick?.('getAnonymousID'));
    } catch {
      readFailed = true;
    }
  }
  const identity = Object.freeze({
    ...(distinctId ? { distinctId } : {}),
    ...(anonymousId ? { anonymousId } : {}),
  });
  if (distinctId && anonymousId)
    return Object.freeze({
      status: 'complete',
      identity: Object.freeze({ distinctId, anonymousId }),
      missingDistinctId: false,
      missingAnonymousId: false,
    });
  return Object.freeze({
    status: 'incomplete',
    identity,
    missingDistinctId: !distinctId,
    missingAnonymousId: !anonymousId,
    reason: !initialized
      ? 'sensors_not_initialized'
      : readFailed
        ? 'read_failed'
        : 'missing_identity_fields',
  });
}
